import React, { useState, SetStateAction } from 'react';
import { Script } from 'gatsby';
import {
  Container,
  Row,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import {
  stateOptions,
  canadianProvinceOptions,
} from '../../utils/displayHelper';
import PlacesAutocomplete, {
  geocodeByAddress,
} from 'react-places-autocomplete';

export type GoogleAddressType = {
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
};

const AddressAutocomplete = (props: {
  selectedAddress: GoogleAddressType;
  setSelectedAddress: React.Dispatch<SetStateAction<GoogleAddressType>>;
  disable?: boolean;
  formInvalid?: boolean;
  setFormInvalid?: React.Dispatch<SetStateAction<boolean>>;
}) => {
  const [geocodeError, setGeocodeError] = useState('');

  function formatGoogleAddress(selectedAddress) {
    const { address_components } = selectedAddress;
    const number = address_components.find((obj) =>
      obj.types.includes('street_number')
    );
    //google formatted addresses don't always have a street number returned
    let street_number = '';
    if (!!number) {
      street_number = `${number.short_name} `;
    }
    const street_route = address_components.find((obj) =>
      obj.types.includes('route')
    ).short_name;
    const address = `${street_number}${street_route}`;
    const city = address_components.find((obj) =>
      obj.types.includes('locality')
    );
    const state = address_components.find((obj) =>
      obj.types.includes('administrative_area_level_1')
    );
    const zip = address_components.find((obj) =>
      obj.types.includes('postal_code')
    );
    return {
      address,
      address2: '',
      city: city.short_name,
      state: state.short_name,
      zip: zip.short_name,
    };
  }

  function handleAddressSelect(address) {
    geocodeByAddress(address)
      .then((res) => {
        setGeocodeError('');
        const formattedAddress = formatGoogleAddress(res[0]);
        const lat = res[0].geometry.location.lat();
        const lng = res[0].geometry.location.lng();
        props.setSelectedAddress({ ...formattedAddress, lat, lng });
      })
      .catch((err) => {
        setGeocodeError(err.message);
      });
  }

  return (
    <>
      <Script src="https://maps.googleapis.com/maps/api/js?key=AIzaSyBvBJkYxNcQdLhh-bYGY_68G6FPDjkUs_A&libraries=places&loading=async" />
      {typeof window !== 'undefined' &&
      !!window.google?.maps?.places &&
      !props.disable ? (
        <PlacesAutocomplete
          value={props.selectedAddress?.address ?? ''}
          onSelect={handleAddressSelect}
          onChange={(address: string) =>
            props.setSelectedAddress({ ...props.selectedAddress, address })
          }
          searchOptions={{
            types: ['address'],
          }}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => {
            const inputProps = getInputProps({
              autoComplete: 'randomStringToPreventBrowserSuggestedAdresses',
              className: 'position-relative',
            });
            return (
              <FormGroup floating>
                <Input
                  {...inputProps}
                  type="text"
                  id="address"
                  name="address"
                  required
                  invalid={
                    !!geocodeError ||
                    (props.formInvalid && !props.selectedAddress.address)
                  }
                  onInvalid={() => {
                    if (props.setFormInvalid) {
                      props.setFormInvalid(true);
                    }
                  }}
                />
                <Label for="address" required>
                  Address
                </Label>
                <FormFeedback>
                  {!!geocodeError ? geocodeError : 'Address is required'}
                </FormFeedback>
                <ListGroup
                  className="position-absolute"
                  style={{ zIndex: '10' }}
                >
                  {loading && <div>Loading...</div>}
                  {suggestions.map((suggestion) => {
                    return (
                      <ListGroupItem
                        {...getSuggestionItemProps(suggestion)}
                        key={suggestion.placeId}
                        className="dp-cursor-pointer dp-hover-bg-secondary"
                      >
                        {suggestion.description}
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </FormGroup>
            );
          }}
        </PlacesAutocomplete>
      ) : (
        //Fallback input if google places api fails
        <FormGroup floating>
          <Input
            name="address"
            id="addressStatic"
            type="text"
            value={props.selectedAddress.address}
            onChange={(e) =>
              props.setSelectedAddress({
                ...props.selectedAddress,
                address: e.target.value,
              })
            }
            invalid={props.formInvalid && !props.selectedAddress.address}
            onInvalid={() => {
              if (props.setFormInvalid) {
                props.setFormInvalid(true);
              }
            }}
          />
          <Label for="addressStatic">Address</Label>
          <FormFeedback>Address is required</FormFeedback>
        </FormGroup>
      )}{' '}
      <FormGroup floating>
        <Input
          name="address2"
          id="address2"
          type="text"
          value={props.selectedAddress?.address2}
          onChange={(e) =>
            props.setSelectedAddress({
              ...props.selectedAddress,
              address2: e.target.value,
            })
          }
        />
        <Label for="address2">Address 2/Suite No.</Label>
      </FormGroup>
      <Container fluid className="px-0">
        <Row className="d-flex">
          <Col className="flex-grow-1">
            <FormGroup floating>
              <Input
                name="city"
                id="city"
                type="text"
                value={props.selectedAddress?.city}
                onChange={(e) =>
                  props.setSelectedAddress({
                    ...props.selectedAddress,
                    city: e.target.value,
                  })
                }
                invalid={props.formInvalid && !props.selectedAddress.city}
                onInvalid={() => {
                  if (props.setFormInvalid) {
                    props.setFormInvalid(true);
                  }
                }}
                required
              />
              <Label for="city" required>
                City
              </Label>
              <FormFeedback>City is required</FormFeedback>
            </FormGroup>
          </Col>
          <Col xs="auto">
            <FormGroup floating>
              <Input
                type="select"
                name="state"
                id="state"
                value={props.selectedAddress?.state}
                onChange={(e) =>
                  props.setSelectedAddress({
                    ...props.selectedAddress,
                    state: e.target.value,
                  })
                }
                required
                disabled={props.disable}
                readOnly={props.disable}
                invalid={props.formInvalid && !props.selectedAddress.state}
                onInvalid={() => {
                  if (props.setFormInvalid) {
                    props.setFormInvalid(true);
                  }
                }}
              >
                <option value=""></option>
                {Object.keys(stateOptions).map((optionKey) => (
                  <option key={optionKey} value={optionKey}>
                    {optionKey}
                  </option>
                ))}
                {Object.keys(canadianProvinceOptions).map((optionKey) => (
                  <option key={optionKey} value={optionKey}>
                    {optionKey}
                  </option>
                ))}
              </Input>
              <Label for="state" required>
                State
              </Label>
              <FormFeedback>State is required</FormFeedback>
            </FormGroup>
          </Col>
          <Col xs="3">
            <FormGroup floating>
              <Input
                name="zip"
                id="zip"
                type="text"
                value={props.selectedAddress?.zip}
                onChange={(e) =>
                  props.setSelectedAddress({
                    ...props.selectedAddress,
                    zip: e.target.value,
                  })
                }
                required
                disabled={props.disable}
                readOnly={props.disable}
                invalid={props.formInvalid && !props.selectedAddress.zip}
                onInvalid={() => {
                  if (props.setFormInvalid) {
                    props.setFormInvalid(true);
                  }
                }}
              />
              <Label for="zip" required>
                Zip
              </Label>
              <FormFeedback>Zip is required</FormFeedback>
            </FormGroup>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddressAutocomplete;
